import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, ButtonBase, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { PHASES, RETENTION_STAGES } from '../constants/stages';
import { RT } from '../theme/retentionTheme';
import StageCard from './StageCard';

// A phase tab's stages as sub-tabs: one stage on screen at a time instead of a long scroll. The open stage is the
// `?stage=` query param, so the stepper, the Home tab and pages that return to the workspace (session preference,
// tutor list) land on it. Default: the student's current stage when it is in this phase, else the phase's first.
//   renderStage(no) → the stage body (inside its StageCard) · aside → optional right column (e.g. From CRM)

export const stageOfPhase = (phase, stageParam, currentStage) => {
  const nos = RETENTION_STAGES.filter((s) => s.phase === phase).map((s) => s.no);
  const asked = Number(stageParam);
  if (nos.includes(asked)) return asked;
  return nos.includes(currentStage) ? currentStage : nos[0];
};

function SubTab({ stage, selected, status, phase, onClick }) {
  const tone = status === 'done' ? RT.success : status === 'now' ? phase.color : RT.faint;
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        flex: '1 0 auto',
        minWidth: 150,
        justifyContent: 'flex-start',
        gap: 1.25,
        px: 1.5,
        py: 1.1,
        borderRadius: 3,
        textAlign: 'left',
        border: `1px solid ${selected ? alpha(phase.color, 0.5) : RT.line}`,
        bgcolor: selected ? phase.light : RT.paper,
        boxShadow: selected ? `0 0 0 3px ${alpha(phase.color, 0.12)}` : 'none',
        transition: 'all .15s',
        '&:hover': { borderColor: alpha(phase.color, 0.5) },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          fontSize: 13,
          fontWeight: 800,
          color: status === 'upcoming' ? RT.muted : '#fff',
          background: status === 'done' ? RT.success : status === 'now' ? phase.gradient : '#EEF1F6',
        }}
      >
        {status === 'done' ? <Icon icon="solar:check-read-linear" width={16} /> : stage.no}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap sx={{ color: selected ? phase.color : RT.ink, lineHeight: 1.3 }}>
          {stage.short}
        </Typography>
        <Typography variant="caption" noWrap sx={{ color: tone, fontWeight: 700 }}>
          {status === 'done' ? 'Done' : status === 'now' ? 'Now' : `Stage ${stage.no}`}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

export default function StageTabs({ phase: phaseNo, currentStage, progressByStage = {}, renderStage, aside }) {
  const [params, setParams] = useSearchParams();
  const phase = PHASES[phaseNo];
  const stages = RETENTION_STAGES.filter((s) => s.phase === phaseNo);
  const selected = stageOfPhase(phaseNo, params.get('stage'), currentStage);
  const index = stages.findIndex((s) => s.no === selected);
  const prev = stages[index - 1];
  const next = stages[index + 1];

  const statusOf = (s) => {
    const st = progressByStage[s.no]?.stage_status;
    if (st === 2 || (st === undefined && s.no < currentStage)) return 'done';
    return s.no === currentStage ? 'now' : 'upcoming';
  };
  // react-router 6.3: setSearchParams takes a value, not an updater
  const open = (no) => {
    const q = new URLSearchParams(params);
    q.set('stage', no);
    setParams(q);
  };

  // a new stage opens at the top
  useEffect(() => {
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  }, [selected]);

  const body = (
    <Stack gap={2}>
      <StageCard key={selected} stageNo={selected} currentStage={currentStage} progress={progressByStage[selected]}>
        {renderStage(selected)}
      </StageCard>
      <Stack direction="row" justifyContent="space-between" gap={1}>
        {prev ? (
          <Button variant="outlined" startIcon={<Icon icon="solar:arrow-left-linear" width={16} />} onClick={() => open(prev.no)}>
            {prev.no} · {prev.short}
          </Button>
        ) : (
          <span />
        )}
        {next && (
          <Button variant="outlined" endIcon={<Icon icon="solar:arrow-right-linear" width={16} />} onClick={() => open(next.no)}>
            {next.no} · {next.short}
          </Button>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Stack gap={2}>
      <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
        <Stack direction="row" gap={1}>
          {stages.map((s) => (
            <SubTab
              key={s.no}
              stage={s}
              phase={phase}
              selected={s.no === selected}
              status={statusOf(s)}
              onClick={() => open(s.no)}
            />
          ))}
        </Stack>
      </Box>
      {aside ? (
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={8}>
            {body}
          </Grid>
          <Grid item xs={12} lg={4}>
            {aside}
          </Grid>
        </Grid>
      ) : (
        body
      )}
    </Stack>
  );
}
